import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComparisonType, LogicalOperatorType } from '@iotv/iotv-v3-types';
import { Alert, Stack } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { ComponentMode } from '../../../../types';
import { Listerlizer } from '../Listerlizer';
import { SimpleList } from '../SimpleList';
import { isVariantA, isVariantB, isVariantC } from './ListerlizerV2Props';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Parses the many variations of the old listerlizer props and returns a listerlizer that will do what is expected
 *
 * `VariantA` is an AdjacentListerlizer (i.e. it queries the database for items to display in the list) that does not specify any special linking items query - it simply gets a list of valid linked things from the context object
 * `VariantB` is an AdjacentListerlizer that runs a specified query on a specified object to find linkable items
 * `VariantC` only queries applicationData to get the list of items to display. I.e. it's just a normal Listerlizer with autoQuery turned off
 *
 */
export const ListerlizerAdapter = (props) => {
    const [{ applicationData: ctxAppData, contextCustomer }] = useContext(AppMainViewContext);
    const [variantCItems, setVariantCItems] = useState([]);
    useEffect(() => {
        if (!ctxAppData || !isVariantC(props))
            return;
        props.query(ctxAppData).then((res) => setVariantCItems(res));
    }, [props.query]);
    if (isVariantA(props)) {
        debug && console.log('ListerlizerAdapter', 'Variant A');
        // Context object is set to matched primary sometimes, but in those cases the user probably wants to query linkable items via context customer, so we provide that instead
        const searchRoot = props.searchRootObject ? [props.searchRootObject] : props.contextObject && contextCustomer ? [contextCustomer] : undefined;
        /** I think OtherObjectType used to be used to infer what kind of link we want to make to the object if it's a group. E.g. Group -> Entity. The same thing is now achieved via adding a filter to the adjacentFilter */
        const adjacentFilter = props.adjacentFilter.objectType === 'Group' && props.adjacentFilter.otherObjectType
            ? {
                ...props.adjacentFilter,
                filterConfig: [
                    ...(props.adjacentFilter.filterConfig ?? []),
                    {
                        filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: `${props.adjacentFilter.otherObjectType}` }],
                        setOperation: LogicalOperatorType.AND,
                    },
                ],
            }
            : props.adjacentFilter;
        const adaptedVariantAProps = {
            ...props,
            adjacentFilter,
            searchRootObjects: searchRoot,
            applicationData: ctxAppData,
            initMode: props.initMode ?? ComponentMode.EDIT,
        };
        return _jsx(Listerlizer, { ...adaptedVariantAProps });
    }
    else if (isVariantB(props)) {
        debug && console.log('ListerlizerAdapter', 'Variant B');
        // Context object is set to matched primary sometimes, but in those cases the user probably wants to query linkable items via context customer, so we provide that instead
        const searchRoot = props.searchRootObject ? [props.searchRootObject] : props.contextObject && contextCustomer ? [contextCustomer] : undefined;
        const adjacentFilter = props.adjacentFilter.objectType === 'Group' && props.adjacentFilter.otherObjectType
            ? {
                ...props.adjacentFilter,
                filterConfig: [
                    ...(props.adjacentFilter.filterConfig ?? []),
                    {
                        filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: `${props.adjacentFilter.otherObjectType}` }],
                        setOperation: LogicalOperatorType.AND,
                    },
                ],
            }
            : props.adjacentFilter;
        const adaptedVariantBProps = {
            ...props,
            linkableItemsBranchingQueryDefinition: props.branchingQueryDefinition,
            linkableItemsResultFilter: props.resultFilter,
            adjacentFilter,
            searchRootObjects: searchRoot,
            applicationData: ctxAppData,
            initMode: props.initMode ?? ComponentMode.EDIT,
        };
        return _jsx(Listerlizer, { ...adaptedVariantBProps });
    }
    else if (isVariantC(props)) {
        debug && console.log('ListerlizerAdapter', 'Variant C');
        return (_jsxs(Stack, { gap: 1, children: [_jsx(Alert, { severity: "warning", children: "This is a Node Edge Mapper list, it should be checked by a developer" }), _jsx(SimpleList, { listItems: variantCItems, viewDefinition: props.viewDefinition })] }));
        // TODO:
        //const adaptedVariantCProps: ListerlizerProps = {
        //  adjacentFilter: {},
        //  transactionFunctions: TransactionFunctions,
        //  userFunctions: UserFunctions,
        //  listItemsQueryOverride: props.query,
        //
        //
        //  applicationData: ctxAppData,
        //}
        //return <Listerlizer {...adaptedVariantCProps} />
    }
    else {
        return _jsx(Alert, { severity: "error", children: "Unable to determine a suitable list from the given inputs!" });
    }
};
